import { ACTIVE_STATUSES } from '../classification';
import type { ExplorationVolume } from '../../types/exploration';
import {
  COVERAGE_NODE_LEVEL_REASONS,
  type CoverageInstallStatus,
  type CoverageSupportStatus,
} from './constants';
import type { CoverageInstance } from './types';

function isNoAutoscalerReason(r: string): boolean {
  const n = String(r).toLowerCase().replace(/[\s_-]/g, '');
  return n === 'agentnotautoscaler' || n === 'noautoscaler';
}

function reasonsOf(v: ExplorationVolume): string[] {
  const out: string[] = [];
  if (Array.isArray(v.datafiableReasons)) out.push(...v.datafiableReasons);
  return out;
}

export function classifyInstance(
  vols: ExplorationVolume[]
): { install: CoverageInstallStatus; support: CoverageSupportStatus } {
  const hasAgent = vols.some(
    (v) => v.usedGiB != null || ACTIVE_STATUSES.has(v.status) || v.status === 'monitored'
  );
  const noAutoscaler = vols.some((v) => reasonsOf(v).some(isNoAutoscalerReason));

  let install: CoverageInstallStatus;
  if (!hasAgent) install = 'nothing';
  else if (noAutoscaler) install = 'sensor';
  else install = 'autoscaler';

  const datafiable = vols.filter((v) => v.datafiable);
  let support: CoverageSupportStatus = 'supported';
  if (datafiable.length === 0) {
    const hasNodeBlocker = vols.some(
      (v) =>
        Array.isArray(v.datafiableReasons) &&
        v.datafiableReasons.some((r) => COVERAGE_NODE_LEVEL_REASONS.has(r))
    );
    support = hasNodeBlocker ? 'nodeUnsupported' : 'noSupported';
  }

  return { install, support };
}

export function buildCoverageInstances(vols: ExplorationVolume[]): CoverageInstance[] {
  const m = new Map<
    string,
    {
      instanceId: string;
      cluster: string | null;
      nodeGroup: string | null;
      region: string | null;
      accountId: string | null;
      accountName: string | null;
      vols: ExplorationVolume[];
    }
  >();

  vols.forEach((v) => {
    if (!v.instanceId) return;
    if (!m.has(v.instanceId)) {
      m.set(v.instanceId, {
        instanceId: v.instanceId,
        cluster: v.cluster || null,
        nodeGroup: v.nodeGroup || null,
        region: v.region || null,
        accountId: v.accountId || null,
        accountName: v.accountName || v.accountId || null,
        vols: [],
      });
    }
    const inst = m.get(v.instanceId)!;
    inst.vols.push(v);
    if (!inst.cluster && v.cluster) inst.cluster = v.cluster;
    if (!inst.nodeGroup && v.nodeGroup) inst.nodeGroup = v.nodeGroup;
    if (!inst.region && v.region) inst.region = v.region;
    if (!inst.accountId && v.accountId) inst.accountId = v.accountId;
    if (!inst.accountName && v.accountName) inst.accountName = v.accountName;
  });

  const out: CoverageInstance[] = [];
  m.forEach((inst) => {
    const cls = classifyInstance(inst.vols);
    let supSize = 0;
    let totSize = 0;
    inst.vols.forEach((v) => {
      const s = v.size || 0;
      totSize += s;
      if (v.datafiable) supSize += s;
    });
    out.push({
      ...inst,
      install: cls.install,
      support: cls.support,
      supportedSize: supSize,
      totalSize: totSize,
      supportedCount: inst.vols.filter((v) => v.datafiable).length,
      totalCount: inst.vols.length,
    });
  });
  return out;
}
